import { isValidEmail } from '../../../../../../utils/email/isValidEmail';
import { shareholderMatchKey } from '../../../../../../utils/shareholder/shareholderMatchKey';

/**
 * Pure CSV/TSV cap table parser used by the setup wizard's import step. Returns either an `error`
 * string or an array of UI-shape shareholder rows ready to be merged with DQI auto-exclusion.
 *
 * Recognizes a flexible set of header aliases for `Name`, `Email`, and `Shares`; treats tab as the
 * separator when present in the header line, otherwise comma.
 */
export interface ParsedCapTableRow {
  id: string;
  name: string;
  email: string;
  shares: number;
  excluded: boolean;
  exclusionType: null;
  attributedTo: null;
  relationship: null;
  importedFromFileName: string;
}

export interface ParseCapTableResult {
  rows?: ParsedCapTableRow[];
  error?: string;
}

const NAME_RE = /^(name|shareholder|holder|full.?name|stakeholder)$/i;
const EMAIL_RE = /^(email|e-?mail|email.?address)$/i;
const SHARES_RE = /^(shares|share.?count|total.?shares|units|quantity|number.?of.?shares|common.?shares)$/i;

const formatImportIssues = (prefix: string, issues: string[]): string => {
  const maxShown = 5;
  const shown = issues.slice(0, maxShown).join(' ');
  const remainder = issues.length > maxShown ? ` (${issues.length - maxShown} more.)` : '';
  return `${prefix} ${shown}${remainder}`;
};

export const parseCapTableCsv = (text: string, fileName: string): ParseCapTableResult => {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) {
    return { error: 'File must have a header row and at least one data row.' };
  }

  const sep = lines[0].includes('\t') ? '\t' : ',';
  const headers = lines[0].split(sep).map((h) => h.replace(/["']/g, '').trim().toLowerCase());
  const nameIdx = headers.findIndex((h) => NAME_RE.test(h));
  const emailIdx = headers.findIndex((h) => EMAIL_RE.test(h));
  const sharesIdx = headers.findIndex((h) => SHARES_RE.test(h));

  if (nameIdx === -1 || emailIdx === -1 || sharesIdx === -1) {
    return {
      error: 'Invalid file. Could not find required columns. Need at minimum a "Name", "Email" and "Shares" column.',
    };
  }

  const rows: ParsedCapTableRow[] = [];
  const emailIssues: string[] = [];
  const duplicateIssues: string[] = [];
  const seenByPair = new Map<string, number>();
  const seenByName = new Map<string, number>();
  const seenByEmail = new Map<string, number>();
  const baseId = Date.now();
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(sep).map((c) => c.replace(/["']/g, '').trim());
    const name = cols[nameIdx];
    const email = cols[emailIdx] ?? '';
    const shares = parseInt(cols[sharesIdx]?.replace(/[^0-9]/g, ''), 10);
    if (name && !isNaN(shares) && shares > 0) {
      const rowLabel = `Row ${i + 1} (${name})`;
      if (!email.trim()) {
        emailIssues.push(`${rowLabel}: missing email`);
      } else if (!isValidEmail(email)) {
        emailIssues.push(`${rowLabel}: invalid email "${email}"`);
      } else {
        const pairKey = shareholderMatchKey(name, email);
        const nameKey = name.trim().toLowerCase();
        const emailKey = email.trim().toLowerCase();
        const firstPairRow = seenByPair.get(pairKey);
        const firstNameRow = seenByName.get(nameKey);
        const firstEmailRow = seenByEmail.get(emailKey);
        if (firstPairRow !== undefined) {
          duplicateIssues.push(`${rowLabel}: duplicate of row ${firstPairRow} (same full name and email)`);
        } else if (firstNameRow !== undefined) {
          duplicateIssues.push(`${rowLabel}: duplicate name (already used in row ${firstNameRow})`);
        } else if (firstEmailRow !== undefined) {
          duplicateIssues.push(`${rowLabel}: duplicate email (already used in row ${firstEmailRow})`);
        } else {
          seenByPair.set(pairKey, i + 1);
          seenByName.set(nameKey, i + 1);
          seenByEmail.set(emailKey, i + 1);
        }
      }
      rows.push({
        id: `sh-${baseId}-${i}`,
        name,
        email: email.trim(),
        shares,
        excluded: false,
        exclusionType: null,
        attributedTo: null,
        relationship: null,
        importedFromFileName: fileName,
      });
    }
  }

  if (rows.length === 0) {
    return { error: 'No valid rows found. Each row needs a name and shares greater than zero.' };
  }
  if (emailIssues.length > 0) {
    return { error: formatImportIssues('Fix email addresses before importing.', emailIssues) };
  }
  if (duplicateIssues.length > 0) {
    return {
      error: formatImportIssues('Remove duplicate names or emails before importing.', duplicateIssues),
    };
  }
  return { rows };
};
